import { Children, memo, useCallback, useMemo, useState } from "react";
import { generatePath } from "react-router";

import { Markdown, rehypeRaw, rehypeSlug, remarkGfm } from "@/lib/react-markdown";

import { PATHS } from "@/routes/paths";

import { ImageModal } from "./components/image-modal";
import { YouTubeEmbed } from "./components/youtube-embed";
import { extractNotionIdFromUrl } from "../../../utils/extract-notion-id";
import { extractYouTubeVideoId } from "../../../utils/extract-youtube-video-id";
import { normalizeMarkdownTable } from "../../../utils/normalize-markdown-table";

type LinkVariant = "card" | "inline";

// VitePress のカスタムコンテナ（::: tip 等）を convertVitepressContainers が変換した div の装飾
const CUSTOM_BLOCK_CLASS_NAMES: Record<string, string> = {
	tip: "border-emerald-500 bg-emerald-50",
	warning: "border-amber-500 bg-amber-50",
	danger: "border-red-500 bg-red-50",
	info: "border-zinc-400 bg-zinc-50",
};

type Props = {
	markdown: string;
	hotelID: string;
	onFaqNavigate?: (notionID: string) => void;
	/**
	 * リンクの表示スタイル。
	 * - `card`（デフォルト）: 従来のカード型ブロックで「リンク」というラベルに置換する（FAQ 詳細用）
	 * - `inline`: 段落内のインラインリンクとして URL テキストをそのまま表示する（ニュースモーダル等）
	 */
	linkVariant?: LinkVariant;
};

export const MarkdownContent = memo(({ markdown, hotelID, onFaqNavigate, linkVariant = "card" }: Props) => {
	const [modalImage, setModalImage] = useState<{ src: string; alt: string } | null>(null);

	const handleImageClick = useCallback((src: string, alt: string) => {
		setModalImage({ src, alt });
	}, []);

	const handleCloseModal = useCallback(() => {
		setModalImage(null);
	}, []);

	// テーブル形式を正規化
	const normalizedMarkdown = useMemo(() => normalizeMarkdownTable(markdown), [markdown]);

	return (
		<div className="markdown-content min-w-0 max-w-none text-zinc-900">
			<ImageModal
				src={modalImage?.src ?? ""}
				alt={modalImage?.alt ?? ""}
				open={modalImage !== null}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
			/>
			<Markdown
				remarkPlugins={[remarkGfm]}
				rehypePlugins={[rehypeRaw, rehypeSlug]}
				components={{
					// 見出し
					h1: ({ children, id }) => (
						<h1
							id={id}
							className="mb-6 mt-10 scroll-mt-20 border-b border-zinc-200 pb-3 text-3xl font-bold first:mt-0"
						>
							{children}
						</h1>
					),
					h2: ({ children, id }) => (
						<h2
							id={id}
							className="mb-4 mt-8 scroll-mt-20 border-b border-zinc-100 pb-2 text-2xl font-semibold"
						>
							{children}
						</h2>
					),
					h3: ({ children, id }) => (
						<h3 id={id} className="mb-3 mt-6 scroll-mt-20 text-xl font-semibold text-zinc-800">
							{children}
						</h3>
					),
					h4: ({ children, id }) => (
						<h4 id={id} className="mb-2 mt-4 scroll-mt-20 text-lg font-semibold text-zinc-800">
							{children}
						</h4>
					),
					h5: ({ children, id }) => (
						<h5 id={id} className="mb-2 mt-3 scroll-mt-20 text-base font-semibold text-zinc-700">
							{children}
						</h5>
					),
					h6: ({ children, id }) => (
						<h6 id={id} className="mb-2 mt-3 scroll-mt-20 text-sm font-semibold text-zinc-600">
							{children}
						</h6>
					),
					// 段落・テキスト
					p: ({ children }) => (
						<p className="whitespace-pre-line text-base leading-7 text-zinc-700">{children}</p>
					),
					strong: ({ children }) => <strong className="font-semibold text-zinc-900">{children}</strong>,
					em: ({ children }) => <em className="italic text-zinc-700">{children}</em>,
					u: ({ children }) => <u className="underline decoration-zinc-400 underline-offset-2">{children}</u>,
					// リスト
					ul: ({ children }) => (
						<ul className="mb-4 ml-6 list-disc space-y-1.5 text-zinc-700 marker:text-zinc-400">
							{children}
						</ul>
					),
					ol: ({ children }) => (
						<ol className="mb-4 ml-6 list-decimal space-y-1.5 text-zinc-700 marker:text-zinc-500">
							{children}
						</ol>
					),
					li: ({ children }) => <li className="pl-1 leading-7">{children}</li>,
					// コード
					code: ({ children, className }) => {
						const isInline = !className;
						if (isInline) {
							return (
								<code className="rounded-md border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-sm text-zinc-800">
									{children}
								</code>
							);
						}
						return <code className={className}>{children}</code>;
					},
					pre: ({ children }) => (
						<pre className="mb-4 overflow-x-auto rounded-lg border border-zinc-200 bg-zinc-50 p-4 font-mono text-sm leading-relaxed [&>code]:border-0 [&>code]:bg-transparent [&>code]:p-0">
							{children}
						</pre>
					),
					// 引用
					blockquote: ({ children }) => (
						<blockquote className="my-4 border-l-4 border-zinc-300 bg-zinc-50 py-2 pl-4 pr-4 italic text-zinc-600">
							{children}
						</blockquote>
					),
					// アコーディオン（details/summary）
					details: ({ children, ...props }) => (
						<details
							className="group my-4 rounded-lg border border-zinc-200 bg-white transition-all open:shadow-sm [&>*:not(summary)]:px-4 [&>*:not(summary)]:pb-4"
							{...props}
						>
							{children}
						</details>
					),
					summary: ({ children }) => (
						<summary className="flex cursor-pointer select-none items-center gap-2 rounded-lg px-4 py-3 font-medium text-zinc-900 transition-colors hover:bg-zinc-50 [&::-webkit-details-marker]:hidden">
							<span className="text-zinc-400 transition-transform group-open:rotate-90">▶</span>
							{children}
						</summary>
					),
					// カスタムコンテナ（::: tip 等）
					div: ({ children, className }) => {
						const classNames = className?.split(" ") ?? [];
						if (!classNames.includes("custom-block")) {
							return <div className={className}>{children}</div>;
						}
						const type = classNames.find((name) => name in CUSTOM_BLOCK_CLASS_NAMES) ?? "info";
						return (
							<div
								className={`my-4 rounded-lg border-l-4 px-4 py-3 [&>p+p]:mt-2 ${CUSTOM_BLOCK_CLASS_NAMES[type]}`}
							>
								{children}
							</div>
						);
					},
					// 水平線
					hr: () => <hr className="my-8 border-t border-zinc-200" />,
					// テーブル
					table: ({ children }) => (
						<div className="my-4 overflow-x-auto">
							<table className="w-full divide-y divide-zinc-200 border border-zinc-200">{children}</table>
						</div>
					),
					// Notion 由来の HTML テーブルは列幅が px で固定されており、狭い表示領域では他の列が 1 文字幅まで潰れる。
					// 幅は捨てて、ブラウザに内容から決めさせる
					col: () => <col />,
					thead: ({ children }) => <thead className="bg-zinc-50">{children}</thead>,
					tbody: ({ children }) => <tbody className="divide-y divide-zinc-100 bg-white">{children}</tbody>,
					tr: ({ children }) => <tr className="hover:bg-zinc-50">{children}</tr>,
					th: ({ children }) => (
						<th className="px-4 py-3 text-left text-sm font-semibold text-zinc-900">{children}</th>
					),
					td: ({ children }) => <td className="px-4 py-3 text-sm text-zinc-700">{children}</td>,
					// 画像・動画
					img: ({ src, alt }) => {
						// 動画ファイルの場合はvideoタグで表示
						const videoExtensions = [".mov", ".mp4", ".webm", ".ogg"];
						const isVideo = src && videoExtensions.some((ext) => src.toLowerCase().endsWith(ext));
						if (isVideo) {
							return (
								<video
									src={src}
									controls
									className="my-4 h-auto max-h-[40rem] w-full max-w-full rounded-lg border border-zinc-200 shadow-sm tablet:max-w-3xl"
								>
									<track kind="captions" />
								</video>
							);
						}

						return (
							<img
								src={src}
								alt={alt || ""}
								className="my-4 h-auto max-h-[40rem] w-full max-w-full cursor-zoom-in rounded-lg border border-zinc-200 object-contain shadow-sm transition-shadow hover:shadow-md tablet:max-w-3xl"
								loading="lazy"
								onClick={() => src && handleImageClick(src, alt || "")}
							/>
						);
					},
					// リンク
					a: ({ href, children }) => {
						// YouTube URL の場合は埋め込みプレーヤーを表示
						const youtubeVideoId = href ? extractYouTubeVideoId(href) : null;
						if (youtubeVideoId) {
							return <YouTubeEmbed videoId={youtubeVideoId} />;
						}

						// 動画ファイルURL の場合は埋め込みプレーヤーを表示
						const videoExtensions = [".mov", ".mp4", ".webm", ".ogg"];
						const isVideoUrl = href && videoExtensions.some((ext) => href.toLowerCase().endsWith(ext));
						if (isVideoUrl) {
							return (
								<video
									src={href}
									controls
									className="my-4 h-auto max-h-[40rem] w-auto max-w-full rounded-lg border border-zinc-200 shadow-sm tablet:max-w-3xl"
								>
									<track kind="captions" />
								</video>
							);
						}

						const notionId = href ? extractNotionIdFromUrl(href) : null;

						const childText = Children.toArray(children)
							.filter((child) => typeof child === "string")
							.join("");
						const isUrlText = childText.startsWith("http://") || childText.startsWith("https://");
						// card バリアントのみ、URL テキストの場合は「リンク」というラベルへ置換する。
						// inline バリアントでは URL をそのまま表示する。
						const displayText = linkVariant === "card" && isUrlText && childText ? "リンク" : children;

						const linkClassName =
							linkVariant === "card"
								? "my-4 block cursor-pointer rounded border-zinc-200 bg-zinc-50 p-6 text-zinc-900 font-bold transition-colors hover:bg-zinc-100 underline"
								: "decoration-blue-300 hover:text-blue-800 hover:decoration-blue-500 break-all font-medium text-blue-600 underline underline-offset-2 transition-colors";

						// Notion URL の場合は内部リンクを生成
						if (notionId) {
							const normalizedId = notionId.replace(/-/g, "");

							// ウィジェット内ナビゲーション
							if (onFaqNavigate) {
								return (
									<button
										type="button"
										onClick={() => onFaqNavigate(normalizedId)}
										className="decoration-blue-300 hover:text-blue-800 hover:decoration-blue-500 font-medium text-blue-600 underline underline-offset-2 transition-colors"
									>
										{displayText}
									</button>
								);
							}

							const internalPath = generatePath(PATHS.FAQ_DETAIL, {
								hotelID,
								notionID: encodeURIComponent(normalizedId),
							});
							return (
								<a href={internalPath} className={linkClassName}>
									{displayText}
								</a>
							);
						}

						// それ以外は外部リンクとしてそのまま表示
						return (
							<a href={href} className={linkClassName} target="_blank" rel="noopener noreferrer">
								{displayText}
							</a>
						);
					},
				}}
			>
				{normalizedMarkdown}
			</Markdown>
		</div>
	);
});

MarkdownContent.displayName = "MarkdownContent";
